import { Component, Vue, Watch } from "vue-property-decorator";
import { getModule } from "vuex-module-decorators";
import { Select, Row, Col, Input, Checkbox, Button, Form, Alert } from "ant-design-vue";
import TextHighlight from "vue-text-highlight";
import { uniq } from "lodash";
import { fmtMsg, isGuid } from "commonui";
import TitleListState from "@/modules/title-list";
import { VCSLevelModel } from "@/models/title-list-model";
import { getGridLevels, getRouteAccessLevel, isAnyVersionSelected } from "@/util/func";
import Grid from "@/components/wijmo-grid/grid/grid.vue";
import {
	MaterialTitleRequestModel,
	MaterialTitleResponseModel,
	ReportManagerService
} from "@/services/text-writer/word-count";
import { VersionGroupModule } from "@/modules/versiongroup";
import { CommonLocale, TextChangeAnalysisLocale } from "@/locales/localeid";
import { IWorkbookCell } from "wijmo/wijmo.xlsx";
import PageTitle from "@/components/page-title/page-title.vue";
import { AccessLevel, CurriculumType } from "@/util/enum";

const startRegex = /^[.,\/#!$%\^&\*@|_;:?{}=\`~()“”"‘’']*/gi;
const endRegex = /[.,\/#!$%\^&\*@|_;:?{}=\`~()“”"‘’']*$/gi;

@Component({
	components: {
		Grid,
		Select,
		Option: Select.Option,
		Row,
		Col,
		TextArea: Input.TextArea,
		Checkbox,
		Button,
		TextHighlight,
		Form,
		FormItem: Form.Item,
		Alert,
		PageTitle
	}
})
export default class TextChangeAnalysis extends Vue {
	public titleListModule = getModule(TitleListState);
	public levels: VCSLevelModel[] = [];
	public endLevels: VCSLevelModel[] = [];
	public selectedStartLevel: number = 0;
	public selectedEndLevel: number = 0;
	public gridStartLevel: number = 0;
	public gridEndLevel: number = 0;
	public selectedLevel: number = 0;
	public analysisReportData: any[] = [];
	public loading: boolean = false;
	public materialTitleData: MaterialTitleResponseModel[] = [];
	public materialLoading: boolean = false;
	public reportService = ReportManagerService();
	public revisedText: string = "";
	public originalRevisedText: string = "";
	public currentText: string = "";
	public locale = TextChangeAnalysisLocale;
	public commonLocale = CommonLocale;
	public format = fmtMsg;
	public revisedWordCount: number = 0;
	public currentWordCount: number = 0;
	public showResultSection: boolean = false;
	public selectedLevelDisplayName: string = "";
	public showCurrent: boolean = true;
	public showRevised: boolean = true;
	public showChanged: boolean = false;
	public currentWords: string[] = [];
	public revisedWords: string[] = [];
	public changedWords: string[] = [];
	private versionGroupModule = VersionGroupModule; // For watch need to create local reference
	public form;
	public noVersion: boolean = false;
	public materialLabelCol = { span: 5 };
	public accessLevel = AccessLevel;

	created() {
		if (!isAnyVersionSelected()) {
			this.noVersion = true;
		}

		this.form = this.$form.createForm(this, { name: "form" });
		this.levels = this.titleListModule.levels;

		// if no GS version selected, set only LS units.
		// if no LS version selected, set only GS units.
		if (!isGuid(VersionGroupModule.selectedGsVersion)) {
			this.levels = this.levels.filter((level) => level.curriculumType === CurriculumType.LittleSEED);
		} else if (!isGuid(VersionGroupModule.selectedLsVersion)) {
			this.levels = this.levels.filter((level) => level.curriculumType === CurriculumType.GrapeSEED);
		}

		this.selectedLevel = this.levels[0].id;
		this.onSelectedLevelChange();

		if (this.levels.length > 0) {
			this.selectedStartLevel = this.levels[0].id;
			this.selectedEndLevel = this.levels[0].id;
			this.setEndLevels(this.selectedStartLevel);
		}
	}

	mounted() {
		this.versionGroupModule.setDisabled(false);
	}

	destroyed() {
		this.versionGroupModule.setDisabled(true);
	}

	get currentRouteAccessLevel() {
		return getRouteAccessLevel(this.$route);
	}

	@Watch("versionGroupModule.selectedGsVersion")
	@Watch("versionGroupModule.selectedLsVersion")
	isVersionUpdated() {
		if (isAnyVersionSelected()) {
			this.noVersion = false;

			if (!isGuid(VersionGroupModule.selectedGsVersion)) {
				this.onNoGsVersion();
			} else if (!isGuid(VersionGroupModule.selectedLsVersion)) {
				this.onNoLSVersion();
			} else {
				this.levels = this.titleListModule.levels;
				this.setEndLevels(this.selectedStartLevel);
			}

			this.fetchMaterials(this.selectedLevel).then(() => {
				this.showResultSection = false;
				this.analysisReportData = []; // reset data on version group change.
				this.selectedLevelDisplayName =
					this.levels.find((level) => level.id === this.selectedLevel)?.displayName || "";
			});
		} else {
			this.noVersion = true;
		}
	}

	onNoGsVersion() {
		this.endLevels = this.titleListModule.levels.filter(
			(level) => level.curriculumType === CurriculumType.LittleSEED
		);
		this.levels = this.titleListModule.levels.filter((level) => level.curriculumType === CurriculumType.LittleSEED);

		const sortedLsLevels = this.titleListModule.levels
			.filter((level) => level.curriculumType === 1)
			.sort((level) => level.unit);

		if (!sortedLsLevels.length) {
			return;
		}

		if (
			this.titleListModule.levels.find((level) => level.id === this.selectedLevel)?.curriculumType ===
			CurriculumType.GrapeSEED
		) {
			this.selectedLevel = sortedLsLevels[0].id;
		}
		if (
			this.titleListModule.levels.find((level) => level.id === this.selectedStartLevel)?.curriculumType ===
			CurriculumType.GrapeSEED
		) {
			this.selectedStartLevel = sortedLsLevels[0].id;
		}
		if (
			this.titleListModule.levels.find((level) => level.id === this.selectedEndLevel)?.curriculumType ===
			CurriculumType.GrapeSEED
		) {
			this.selectedEndLevel = sortedLsLevels[0].id;
		}
	}

	onNoLSVersion() {
		this.endLevels = this.titleListModule.levels.filter(
			(level) => level.curriculumType === CurriculumType.GrapeSEED
		);
		this.levels = this.titleListModule.levels.filter((level) => level.curriculumType === CurriculumType.GrapeSEED);

		const sortedGsLevels = this.titleListModule.levels
			.filter((level) => level.curriculumType === 0)
			.sort((level) => level.unit);

		if (!sortedGsLevels.length) {
			return;
		}

		if (
			this.titleListModule.levels.find((level) => level.id === this.selectedLevel)?.curriculumType ===
			CurriculumType.LittleSEED
		) {
			this.selectedLevel = sortedGsLevels[0].id;
		}
		if (
			this.titleListModule.levels.find((level) => level.id === this.selectedStartLevel)?.curriculumType ===
			CurriculumType.LittleSEED
		) {
			this.selectedStartLevel = sortedGsLevels[0].id;
		}
		if (
			this.titleListModule.levels.find((level) => level.id === this.selectedEndLevel)?.curriculumType ===
			CurriculumType.LittleSEED
		) {
			this.selectedEndLevel = sortedGsLevels[0].id;
		}
	}

	onSelectedLevelChange() {
		this.fetchMaterials(this.selectedLevel).then(() => {
			this.showResultSection = false;
		});
		this.selectedLevelDisplayName = this.levels.find((level) => level.id === this.selectedLevel)?.displayName || "";
	}

	onStartUnitChange() {
		this.setEndLevels(this.selectedStartLevel);
	}

	setEndLevels(startLevel) {
		const selectedStartLevel = this.levels.find((level) => level.id === startLevel);

		if (!selectedStartLevel) {
			return;
		}

		this.endLevels = this.levels.filter(
			(level) =>
				level.curriculumType <= selectedStartLevel.curriculumType && level.unit >= selectedStartLevel.unit
		);

		const selectedEndLevel = this.levels.find((level) => level.id === this.selectedEndLevel);

		if (!selectedEndLevel) {
			return;
		}

		if (
			!(
				selectedEndLevel.curriculumType <= selectedStartLevel.curriculumType &&
				selectedEndLevel.unit >= selectedStartLevel.unit
			)
		) {
			this.selectedEndLevel = startLevel;
		}
	}

	fetchMaterials(levelId: number) {
		this.materialLoading = true;
		const selectedLevel = this.titleListModule.levels.find((level) => level.id === levelId);

		const materialTitleRequestModel: MaterialTitleRequestModel = {
			startLevel: selectedLevel?.unit || 0,
			endLevel: selectedLevel?.unit || 0,
			startLevelCurriculumType: selectedLevel?.curriculumType as CurriculumType,
			endLevelCurriculumType: selectedLevel?.curriculumType as CurriculumType,
			selectedGSVersionGroupId:
				VersionGroupModule.selectedGsVersion !== "-1" ? VersionGroupModule.selectedGsVersion : null,
			selectedLSVersionGroupId:
				VersionGroupModule.selectedLsVersion !== "-1" ? VersionGroupModule.selectedLsVersion : null
		};

		return this.reportService.getMaterialTitle(materialTitleRequestModel).then((materialTitleData) => {
			if (materialTitleData.length) {
				this.materialTitleData = materialTitleData.sort((a, b) => (a.materialTitle < b.materialTitle ? -1 : 1));
				this.form.setFieldsValue({ selectedMaterial: materialTitleData[0].contentId });
			} else {
				this.materialTitleData = [];
				this.form.setFieldsValue({ selectedMaterial: "" });
			}

			this.materialLoading = false;
		});
	}

	getReport(e) {
		if (e) {
			e.preventDefault();
		}

		this.form.validateFields((err, values) => {
			if (!err) {
				this.loading = true;
				const startLevel = this.levels.find((level) => level.id == this.selectedStartLevel);
				const endLevel = this.levels.find((level) => level.id == this.selectedEndLevel);
				this.originalRevisedText = values.revisedText;

				this.revisedWords = this.originalRevisedText.split("\n").flatMap((text) =>
					text
						.toLowerCase()
						.replace(/[—]+/g, " ")
						.split(" ")
						.map((item) => item.trim().replace(startRegex, "").replace(endRegex, ""))
						.filter((item) => item && /\w/g.test(item))
						.map((text) => text.trim())
				);

				this.revisedWordCount = this.revisedWords.length;

				const uniqueWords = uniq(this.revisedWords);

				const request = {
					startLevel: startLevel?.unit || 0,
					endLevel: endLevel?.unit || 0,
					startLevelCurriculumType: startLevel?.curriculumType as CurriculumType,
					endLevelCurriculumType: endLevel?.curriculumType as CurriculumType,
					contentId: values.selectedMaterial,
					selectedGSVersionGroupId:
						VersionGroupModule.selectedGsVersion !== "-1" ? VersionGroupModule.selectedGsVersion : null,
					selectedLSVersionGroupId:
						VersionGroupModule.selectedLsVersion !== "-1" ? VersionGroupModule.selectedLsVersion : null,
					revisedWords: uniqueWords.join(",")
				};

				this.reportService
					.getTextChangeAnalysisReport(request)
					.then((res) => {
						this.gridStartLevel = this.selectedStartLevel;
						this.gridEndLevel = this.selectedEndLevel;
						this.currentText = res.currentText;

						this.currentWords = this.currentText.split("\n").flatMap((text) =>
							text
								.toLowerCase()
								.replace(/[—]+/g, " ")
								.split(" ")
								.map((item) => item.trim().replace(startRegex, "").replace(endRegex, ""))
								.filter((item) => item && /\w/g.test(item))
								.map((text) => text.trim())
						);

						this.analysisReportData = this.setSelectedLevelCount(
							res.data,
							this.currentWords,
							this.revisedWords
						);
						this.calculateChangedWords(this.currentWords, this.revisedWords);
						this.loading = false;
						this.showResultSection = true;
					})
					.catch(() => {
						this.loading = false;
					});
			}
		});
	}

	calculateChangedWords(currentWords: string[], revisedWords: string[]) {
		const uniqCurrentWords = uniq(currentWords);
		const uniqRevisedWords = uniq(revisedWords);

		// present in revised words but not in current words
		this.changedWords = uniqRevisedWords.filter((revisedWord) => uniqCurrentWords.indexOf(revisedWord) < 0).sort();
	}

	get gridLevels() {
		const gridLevels = getGridLevels(this.titleListModule.levels, this.gridStartLevel, this.gridEndLevel);
		return gridLevels;
	}

	getCellValue(value, displayName) {
		if (value.item[displayName] == null) {
			return 0;
		} else {
			return value.item[displayName];
		}
	}

	setSelectedLevelCount(data, currentWords, revisedWords) {
		const selectedLevel = this.levels.find((level) => level.id === this.selectedLevel);

		if (!selectedLevel) {
			return;
		}

		return data.map((item) => {
			item["current"] = currentWords.filter((text) => item["Word"] === text).length;
			item["revised"] = revisedWords.filter((text) => item["Word"] === text).length;
			item["diff"] = item["revised"] - item["current"];
			return item;
		});
	}

	get exportOptions() {
		return {
			formatCell: (cell: IWorkbookCell, binding: string) => {
				if (binding !== "Word" && binding !== "VpcLevels" && !cell.value) {
					cell.value = 0;
				}
			},
			filename: this.format(this.locale.AnalysisExportFileName)
		};
	}

	filterOption(input, option) {
		return option.componentOptions.children[0].text.toLowerCase().indexOf(input.toLowerCase()) >= 0;
	}
}
